import BaseComponent from '../base-component/base-component'
import { bodyElement } from '../../components/common/body-element'
import { parseHTML } from '../../utils/dom'

class VariantPicker extends BaseComponent {
  constructor() {
    super()

    this.pickupAvailability = document.getElementById(
      `product-pickup-availability-${this.dataset.productId}-${this.dataset.section}`,
    )

    this.elements = [
      'sticky-product-buy-btn',
      'product-buy-btn',
      'product-quantity',
    ]
  }

  connectedCallback() {
    this.updateVariant()

    setTimeout(() => {
      this.updateOptionsAvailable()
      this.setFocusListeners()
    }, 100)

    if (this.dataset.selectedVariantId) {
      this.updateFirstMediaByVariant(this.variant, true)
    }

    this.addEventListener('change', this.handleVariantChange)
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleVariantChange)
  }

  handleVariantChange = () => {
    this.updateForm()

    this.setInventoryStatusAvailable(this.variant)

    if (!this.variant) {
      this.setButtonsUnavailable()
      return
    }

    this.form.setDisable(true)
    this.form.setLoading(true)

    if (this.pickupAvailability) {
      this.updatePickupAvailability()
    }

    this.updateURL()
    this.updateVariantInput()
    this.updateProduct()

    this.updateFirstMediaByVariant(this.variant)
  }

  updateLabels() {
    const inputContainers = this.querySelectorAll(
      '[data-variant-picker-option-inputs]',
    )
    const labelContainers = this.querySelectorAll(
      '[data-variant-picker-option-labels]',
    )

    inputContainers.forEach((inputContainer, idx) => {
      const labels = [...labelContainers[idx].querySelectorAll('label')]
      const inputs = [...inputContainer.querySelectorAll('input')]
      const checkedInput = this.getCheckedInput(inputs)
      const dropdownLabel = labelContainers[idx].querySelector(
        '[data-dropdown-variant-picker-label]',
      )

      if (dropdownLabel) {
        dropdownLabel.toggleAttribute('no-available', !this.variant?.available)
      }

      labels.forEach(label => {
        const isMatched = label.getAttribute('for') === checkedInput.id

        if (dropdownLabel && isMatched) {
          const dropdown = dropdownLabel.closest('dropdown-opener')
          const clonedLabel = label.cloneNode(true)

          dropdownLabel.replaceChildren(...clonedLabel.childNodes)

          dropdown.trigger()
        }

        label.classList.toggle('selected', isMatched)
      })
    })
  }

  updateURL() {
    if (!this.variant || this.dataset.updateUrl === 'false') {
      return
    }

    window.history.replaceState(
      {},
      '',
      `${this.dataset.url}?variant=${this.variant.id}`,
    )
  }

  setInventoryStatusAvailable(isAvailable) {
    const productStockInner = document.getElementById(
      `product-stock-inner-${this.dataset.productId}-${this.dataset.section}`,
    )

    if (productStockInner) {
      productStockInner.classList.toggle('hidden', !isAvailable)
    }
  }

  setButtonsUnavailable() {
    this.form.setDisable(true)
    this.form.setButtonText(
      window.auroraThemeLocales.variantStrings.unavailable,
    )
  }

  updateVariant() {
    const options = this.querySelectorAll('[data-variant-picker-option-inputs]')
    const variants = this.getVariants()

    const selectedOptions = [...options].map(option => {
      const inputs = [...option.querySelectorAll('input')]
      const found = this.getCheckedInput(inputs)

      return found.value
    })

    this.variant = variants.find(({ options }) => {
      return !options
        .map((option, index) => selectedOptions[index] === option)
        .includes(false)
    })
  }

  setFocusListeners() {
    if (this.dataset.type === 'dropdown') {
      const labels = [...this.querySelectorAll('float-element')]
        .map(dropdown => {
          return [...dropdown.element.querySelectorAll('label')]
        })
        .reduce((acc, labels) => [...acc, ...labels], [])

      this.setListenersForLabels(labels)
    } else {
      const labels = [...this.querySelectorAll('label')]

      this.setListenersForLabels(labels)
    }
  }

  handleLabelFocus = event => {
    const inputId = event.target.getAttribute('for')
    const input = this.querySelector(`input[id="${inputId}"]`)

    input.dispatchEvent(new MouseEvent('click', { button: 0, which: 1 }))

    document.addEventListener('keydown', this.handleLabelKeydown)
  }

  setListenersForLabels(labels) {
    labels.forEach(label => {
      label.addEventListener('focus', this.handleLabelFocus)
    })
  }

  removeListenersForLabels(labels) {
    labels.forEach(label => {
      label.addEventListener('focus', this.handleLabelFocus)
    })
  }

  handleLabelKeydown = event => {
    const labelContainer = event.target.closest(
      '[data-variant-picker-option-labels]',
    )

    const isArrowKey =
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.key === 'ArrowUp' ||
      event.key === 'ArrowDown'

    if (!isArrowKey) return
    if (!labelContainer) {
      document.removeEventListener('keydown', this.handleLabelKeydown)

      return
    }

    const labels = [...labelContainer.querySelectorAll('label')]
    const index = labels.findIndex(label => label === e.target)

    const currentLabel = labels[index]
    const firstLabel = labels[0]
    const lastLabel = labels[labels.length - 1]
    const nextLabel = labels[index + 1]
    const prevLabel = labels[index - 1]

    if (labels.length !== 1) {
      e.preventDefault()
    }

    const setActiveLabel = newLabel => {
      currentLabel.removeAttribute('tabindex')
      newLabel.setAttribute('tabindex', 0)
      newLabel.focus()
    }

    const changeActiveLabelOnNext = () => {
      const label = nextLabel || firstLabel

      setActiveLabel(label)
    }

    const changeActiveLabelOnPrev = () => {
      const label = prevLabel || lastLabel

      setActiveLabel(label)
    }

    const callback = {
      ArrowLeft: changeActiveLabelOnPrev,
      ArrowRight: changeActiveLabelOnNext,
      ArrowUp: changeActiveLabelOnPrev,
      ArrowDown: changeActiveLabelOnNext,
    }[e.key]

    callback()
  }

  updateOptionsAvailable() {
    const options = this.querySelectorAll('[data-variant-picker-option-inputs]')

    const getOptionsByIndex = index => {
      return options[index]
        ? [...options[index].querySelectorAll('input')]
        : null
    }

    const firstOptions = getOptionsByIndex(0)
    const secondOptions = getOptionsByIndex(1)
    const thirdOptions = getOptionsByIndex(2)

    const variants = this.getVariants()

    const getVariantsByOption = (...prevOptions) => {
      return variants.reduce((result, variant) => {
        const isIncludePrevOptions = prevOptions
          ? prevOptions.every(prevOption =>
              variant.options.includes(prevOption.value),
            )
          : true

        return variant.available && isIncludePrevOptions
          ? [...result, variant.options]
          : result
      }, [])
    }

    const getDropdownElementBySelector = selector => {
      return [...this.querySelectorAll('float-element')]
        .find(dropdown => {
          return dropdown.element.querySelector(selector)
        })
        .element.querySelector(selector)
    }

    const setOptions = (options, possibleVariants) => {
      for (const option of options) {
        const value = option.value

        let available = false

        possibleVariants.forEach(possibleVariant => {
          if (possibleVariant.includes(value)) {
            available = true
          }
        })

        const labelSelector = `label[for="${option.id}"]`
        const hasLabel = this.querySelector(labelSelector)

        const label = hasLabel
          ? this.querySelector(labelSelector)
          : getDropdownElementBySelector(labelSelector)

        label.toggleAttribute('no-available', !available)
      }
    }

    setOptions(firstOptions, getVariantsByOption())

    if (secondOptions) {
      const firstSelectedOption = this.getCheckedInput(firstOptions)

      setOptions(secondOptions, getVariantsByOption(firstSelectedOption))
    }

    if (thirdOptions) {
      const firstSelectedOption = this.getCheckedInput(firstOptions)
      const secondSelectedOption = this.getCheckedInput(secondOptions)

      setOptions(
        thirdOptions,
        getVariantsByOption(firstSelectedOption, secondSelectedOption),
      )
    }
  }

  getVariants() {
    const variantsJSON = this.querySelector('[data-variant-picker-variants]')
    const variants = JSON.parse(variantsJSON.textContent)

    return variants
  }

  updateFirstMediaByVariant(variant, isForceAnimation) {
    const mediaList = document.querySelectorAll(
      `#product-media-${this.dataset.productId}-${this.dataset.section}`,
    )

    mediaList.forEach(media =>
      media.updateFirstMediaByVariant(variant, isForceAnimation),
    )
  }

  updateForm() {
    this.updateVariant()
    this.updateLabels()
    this.updateOptionsAvailable()
  }

  updateVariantInput() {
    const input = this.form.variantInput

    input.value = this.variant.id
    input.dispatchEvent(new Event('change', { bubbles: true }))
  }

  updatePickupAvailability() {
    this.pickupAvailability.updateVariantId(this.variant.id)
    this.pickupAvailability.fetchAvailability()
  }

  updateProduct() {
    const queryUrl = `${this.dataset.url}?variant=${this.variant.id}&section_id=${this.dataset.sectionIdWithoutPrefix}`

    this.updateBlocksByVariantId(this.variant.id)

    fetch(queryUrl)
      .then(response => response.text())
      .then(responseText => {
        if (!this.form) {
          return
        }

        const html = parseHTML(responseText)
        const isAvailable = this.variant.available

        this.form.setDisable(!isAvailable)
        this.form.setLoading(false)

        this.updateElements(html)
      })
      .catch(() => {
        if (!bodyElement.notification) {
          return
        }

        bodyElement.notification.show(
          'Error in variant picker component',
          'warning',
        )
      })
  }

  updateElements(html) {
    this.elements.forEach(block => {
      const id = `${block}-${this.dataset.productId}-${this.dataset.section}`
      const target = document.getElementById(id)

      if (target) {
        const newTarget = html.getElementById(id)
        const clonedNewTarget = newTarget.cloneNode(true)

        target.replaceChildren(...clonedNewTarget.childNodes)
      }
    })
  }

  updateBlocksByVariantId(variantId) {
    const blocks = ['product-price', 'product-sku', 'product-stock']

    blocks.forEach(block => {
      const target = document.getElementById(
        `${block}-${this.dataset.productId}-${this.dataset.section}`,
      )

      if (target) {
        target.updateByVariantId(variantId)
      }
    })
  }

  getCheckedInput(inputs) {
    return inputs.find(({ checked }) => checked)
  }

  get form() {
    return document.getElementById(
      `product-form-component-${this.dataset.productId}-${this.dataset.section}`,
    )
  }
}

export default VariantPicker
